import { QuestType, QuestStatus, QuestActivityType } from 'src/enums/quests';
import { Rating } from './rating';
import { IAttachment } from './attachments';
import { ReviewStatus, WorkType } from 'src/enums/work';
import { TaskSummary } from './task';

export interface Quest {
    Project: string;
    'Time created': Date;
    'Quest name': string;
    'Net working time': number;
    Description: string;
}

export type SuccessCriteria = {
    title: string;
    description: string;
    explanation?: string | null;
    checked?: boolean;
};

export interface QuestActivity {
    id: number;
    user: string;
    activityType: QuestActivityType;
    details: {
        text?: string;
        status?: QuestStatus;
        statusChange?: {
            from: QuestStatus;
            to: QuestStatus;
        };
        ratings?: Rating[];
        workType?: WorkType;
        taskTitle?: string;
        taskId?: string;
        taskToCreate?: string[];
        tasksDependencies?: {
            [taskTitle: string]: (string | number)[];
        };
        netWorkTime?: string;
        grossWorkTime?: string;
        rejectionReason?: string;
        solveByReviewer?: boolean;
        seniorReviewerName?: string;
        blockerDescription?: string;
        attachments?: IAttachment[];
        reviewStatus?: ReviewStatus;
        priority?: {
            from: number;
            to: number;
        };
    };
    created: string;
}

export interface RequestModificationsRequest {
    feedback: string;
    attachmentList?: number[];
}

export interface CreateDraftQuestRequest {
    projectId: number;
    description: string;
    attachmentList?: number[];
    questId?: string;
}

export interface RequestApproveRequest {
    feedback: string;
    merge?: boolean;
}

export interface AsyncJobResponse {
    jobId: string;
    status: 'pending' | 'running' | 'ready' | 'failed';
    ready: boolean;
    result: any | null;
    error: string | null;
}

export enum BeehiveHumanReviewStatus {
    NOT_SENT = 1,
    IN_PROGRESS = 2,
    DONE = 3
}

export const BEEHIVE_HUMAN_REVIEW_TEXT: Record<
    BeehiveHumanReviewStatus,
    string
> = {
    [BeehiveHumanReviewStatus.NOT_SENT]: 'Not Sent',
    [BeehiveHumanReviewStatus.IN_PROGRESS]: 'In Progress',
    [BeehiveHumanReviewStatus.DONE]: 'Done'
};

export function resolveBeehiveHumanReviewStatus(
    status: BeehiveHumanReviewStatus | null | undefined
): BeehiveHumanReviewStatus {
    return status ?? BeehiveHumanReviewStatus.NOT_SENT;
}

export interface UpdateQuestRequest {
    status?: QuestStatus;
    priority?: number;
}

export interface QuestSchema {
    id: string;
    created: string;
    delegatingUserId: string;
    description: string;
    title: string;
    status: QuestStatus;
    questType: QuestType;
    links: {
        documentation: string;
    };
    projectId?: number;
    delegationTimeSeconds?: number | null;
    successCriteria: SuccessCriteria[];
    activities: QuestActivity[];
    iterations?: number;
    progress?: number;
    averageRatingScore?: number;
    ratingAuthorizationCode: string | null;
    attachments?: IAttachment[];
    priority: number;
    statusSummary?: {
        text: string;
        timestamp: string;
    };
    questSolutionPrs?: QuestSolutionPullRequest[];
    tasks?: TaskSummary[];
    planningSessionId?: string | null;
    llmCostUsd?: number;
    contributorCostUsd?: number;
    isMerged?: boolean;
    beehiveHumanReviewStatus?: BeehiveHumanReviewStatus | null;
}

export interface QuestSolutionPullRequest {
    id: string;
    prUrl: string;
}

export interface QuestSolution {
    id: string;
    notes: string;
    userId: string;
    questSolutionPrs: QuestSolutionPullRequest[];
    ratingAuthorizationCode: string | null;
    attachments: IAttachment[];
}

export const successCriteriaList: SuccessCriteria[] = [
    {
        title: 'Responsive Design',
        description:
            'Ensure the website or application looks and works well across various devices and screen sizes.'
    },
    {
        title: 'Cross-browser Compatibility',
        description:
            'Ensure the website or application functions correctly on different desktop & mobile web browsers.'
    },
    {
        title: 'Data integrity',
        description: 'Verify data accuracy and consistency.'
    },
    {
        title: 'Performance Optimization',
        description: 'Aim for fast loading times and smooth performance.'
    },
    {
        title: 'Accessibility',
        description:
            'Ensure the website or app is accessible to users with disabilities.'
    },
    {
        title: 'Security Measures',
        description: 'Implement custom security measures.'
    },
    {
        title: 'Custom Documentation',
        description: 'Provide additional documentation for the codebase.'
    }
];
